<script setup>
  import { ref, computed, onMounted, onUnmounted, nextTick, getCurrentInstance } from "vue";
  import { storeToRefs } from "pinia";
  import { Eraser, MapPin, X, Trash, Loader2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Switch } from "@/components/ui/switch";
  import { Select } from "@/components/episto-ui/select";
  import { Badge } from "@/components/ui/badge";
  import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
  import { isEmpty } from "@utils/toolBox";
  import { useLocationsStore } from "@stores/mAudience/locations";
  import { LabeledField } from "@/components/episto-ui/labeled-field";
  import { LightListContainer } from "@/components/episto-ui/list";
  import { LightListItem } from "@/components/episto-ui/list";
  import LocationBulkAdd from "@/components/MCampaign/Audiences/AudienceEdit/LocationSection/LocationBulkAdd/LocationBulkAdd.vue";

  const locationsStore = useLocationsStore();

  const locationOptionsLoading = ref(false);
  const isLocationsOpen = ref(false);
  const instance = getCurrentInstance();
  const cable = instance.proxy.$cable;

  const {
    getMAudienceSegmentByLocation,
    getCountryOptions,
    getMAudienceCountry,
    getLocationOptions,
    getMAudienceLocations,
    bulkAddErrors,
    bulkAddLoading,
  } = storeToRefs(locationsStore);

  const {
    updateMAudienceCountry,
    updateMAudienceSegmentByLocation,
    isLocationSelected,
    updateLocationOptions,
    updateMAudienceLocations,
    addAllRegions,
    removeLocation,
    removeAllLocations,
    canHaveRadius,
    hasRadius,
    createRadius,
    removeRadius,
    getMAudienceLocationRadius,
    updateMAudienceLocationRadius,
    adsChannelHandlers,
    resetBulkAddErrors,
  } = locationsStore;

  const locationSelectOptions = computed(() =>
    getLocationOptions.value.map((option) => ({
      ...option,
      disabled: isLocationSelected(option.value),
    })),
  );

  const locationCountLabel = computed(() => {
    const count = getMAudienceLocations.value.length || 0;
    return count > 1 ? `${count} locations` : `${count} location`;
  });

  const handleClick = async () => {
    isLocationsOpen.value = true;
    await addAllRegions();
  };

  const handleLocationInput = async (value) => {
    locationOptionsLoading.value = true;
    await updateLocationOptions(value);
    locationOptionsLoading.value = false;
  };

  const handleLocationSelect = async (value) => {
    isLocationsOpen.value = true;
    await updateMAudienceLocations(value);
  };

  const getMin = (location) => (location.type === "city" ? 17 : 1);
  const getLocationLabel = (location) => {
    let locationLabel = location.name;
    if (location.region != null) locationLabel += ` (${location.region})`;
    return locationLabel;
  };
  const getLocationType = (location) => location.type?.replaceAll("_", " ");

  onMounted(() => {
    cable.subscribe({ channel: "AdsChannel" });
    cable._addChannel("AdsChannel", adsChannelHandlers(cable), {
      _uid: instance.uid,
      _value: instance.proxy,
    });
    nextTick(() => {
      if (getMAudienceLocations.value?.length) isLocationsOpen.value = true;
    });
  });

  onUnmounted(() => {
    cable.unsubscribe("AdsChannel");
    resetBulkAddErrors();
  });
</script>

<template>
  <h4 class="heading-section mt-4 mb-3">Locations</h4>
  <label class="flex items-center gap-2 mb-4">
    <Switch
      :model-value="getMAudienceSegmentByLocation"
      @update:model-value="updateMAudienceSegmentByLocation" />
    <span class="text-sm">Segment by location</span>
  </label>
  <LabeledField label="Country">
    <Select
      :model-value="getMAudienceCountry"
      :options="getCountryOptions"
      filterable
      @update:model-value="updateMAudienceCountry" />
  </LabeledField>
  <LabeledField label="Add locations">
    <div class="mb-2">
      <Button
        variant="outline"
        size="sm"
        @click="handleClick">
        All regions
      </Button>
    </div>
    <div class="flex items-center gap-1.5 h-10">
      <Select
        class="flex-1"
        remote
        filterable
        placeholder="Search locations..."
        :options="locationSelectOptions"
        :loading="locationOptionsLoading"
        @search="handleLocationInput"
        @update:model-value="handleLocationSelect" />
      <LocationBulkAdd />
    </div>
    <Button
      v-if="bulkAddLoading"
      variant="ghost"
      size="sm"
      disabled>
      <Loader2 class="h-4 w-4 mr-1 animate-spin" />
      Locations are being added...
    </Button>
    <div
      v-if="!isEmpty(bulkAddErrors) && !bulkAddLoading"
      class="mt-2 rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
      These locations couldn't be added: {{ bulkAddErrors.join(", ") }}
    </div>
  </LabeledField>
  <Collapsible
    v-if="getMAudienceLocations?.length"
    :open="isLocationsOpen"
    @update:open="isLocationsOpen = $event">
    <CollapsibleTrigger class="flex w-full items-center justify-between py-2 text-sm font-medium">
      Selected locations
    </CollapsibleTrigger>
    <CollapsibleContent>
      <div class="flex items-center justify-between mb-2">
        <Badge variant="secondary">
          {{ locationCountLabel }} selected
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          @click="removeAllLocations">
          <Eraser class="h-4 w-4 mr-1" />
          <span>Remove all locations</span>
        </Button>
      </div>
      <LightListContainer>
        <LightListItem
          v-for="(location, index) in getMAudienceLocations"
          :key="location.key"
          :title="getLocationLabel(location)"
          :subtitle="getLocationType(location)">
          <template #actions>
            <Button
              v-if="canHaveRadius(index) && !hasRadius(index)"
              variant="ghost"
              size="sm"
              @click="createRadius(index)">
              <MapPin class="h-4 w-4 mr-1" />
              <span>Add radius</span>
            </Button>
            <div
              v-else-if="hasRadius(index)"
              class="flex items-center gap-2">
              <Input
                :model-value="getMAudienceLocationRadius(index)"
                type="number"
                class="w-20"
                :min="getMin(location)"
                :max="80"
                @update:model-value="updateMAudienceLocationRadius(index, Number($event))" />
              <Button
                variant="destructive"
                size="icon-sm"
                @click="removeRadius(index)">
                <X class="h-3 w-3" />
              </Button>
              <span class="text-sm">km</span>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              @click="removeLocation(index)">
              <Trash class="h-4 w-4" />
            </Button>
          </template>
        </LightListItem>
      </LightListContainer>
    </CollapsibleContent>
  </Collapsible>
</template>
